import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import usePagination from 'src/registry/hooks/usePagination';
import { createColumnHelper } from '@tanstack/react-table';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import useSorting from 'src/registry/hooks/useSorting';

import { useListWebhookEvents } from 'src/features/webhook-events/api/useListWebhookEvents';
import { WebhookEvent } from 'src/features/webhook-events/utils/types';
import { webhookEventsMessages } from 'src/features/webhook-events/utils/messages';
import { generalMessages } from 'src/utils/messages';
import { WEBHOOK_EVENT_STATUS_BADGE_MAP } from 'src/features/webhook-events/utils/constants';
import { webhookEndpointsMessages } from 'src/features/webhook-endpoints/utils/messages';
import useFilters from 'src/registry/hooks/useFilters';
import { useListWebhookEndpoints } from 'src/features/webhook-endpoints/api/useListWebhookEndpoints';

const WebhookEventsTable = () => {
    const { formatMessage } = useIntl();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'created_at', desc: false }],
    });
    const { queryFiltersOptions, ...filters } = useFilters();

    const {
        data: webhookEvents,
        isLoading,
        isError,
    } = useListWebhookEvents({
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
    });

    const { data: listWebhookEndpoints } = useListWebhookEndpoints({});

    const columnHelper = createColumnHelper<WebhookEvent>();

    const columns = [
        columnHelper.accessor('endpoint', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            cell: ({ row }) => {
                return row.original.endpoint.url ?? '-';
            },
            meta: {
                title: formatMessage(webhookEndpointsMessages.url),
                options: listWebhookEndpoints?.results?.map((endpoint) => ({
                    value: endpoint.id,
                    label: endpoint.url,
                })),
            },
        }),
        columnHelper.accessor('topic', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(webhookEventsMessages.topic),
            },
        }),
        columnHelper.accessor('webhook_id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(webhookEventsMessages.webhookId),
            },
        }),
        columnHelper.accessor('status', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(generalMessages.status),
                badgeOptions: WEBHOOK_EVENT_STATUS_BADGE_MAP,
            },
        }),
        columnHelper.accessor('attempt_count', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(webhookEventsMessages.attemptCount),
            },
        }),
        columnHelper.accessor('next_retry_at', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(webhookEventsMessages.nextRetryAt),
            },
        }),
        columnHelper.accessor('last_attempted_at', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(webhookEventsMessages.lastAttemptedAt),
            },
        }),
    ];

    return (
        <DataTable
            columns={columns}
            data={webhookEvents}
            semantics={{
                title: formatMessage(webhookEventsMessages.webhookEvents),
                entryName: formatMessage(webhookEventsMessages.webhookEvent).toLowerCase(),
                entryNames: formatMessage(webhookEventsMessages.webhookEvents).toLowerCase(),
                placeholder: formatMessage(webhookEventsMessages.webhookEvent).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            filters={filters}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
};

export default WebhookEventsTable;
